import React, { ChangeEvent, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { StatusCode } from "api/protocols";
import { showError, showSuccess } from "redux/actions/ui";
import { useAppDispatch } from "hooks/redux";

import Page from "components/page/Page";
import Card from "components/card/Card";
import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import { Select } from "components/select/index";
import { handleChangeEventInput } from "helpers/Helpers";
import Input from "components/form/input/Input";
import { ButtonNew as Button } from "components/buttonNew";
import TextField from "components/form/textField/TextField";
import {
  IPostAccommodationUnitType,
  PostAccommodationUnitType,
} from "api/rpc/2024-04/facilityAdmin/accommodation/unitTypes";
import { GetAccommodationCategories } from "api/rpc/2024-04/facilityAdmin/accommodation/categories";
import { IAccommodationCategory } from "../categories/Categories";

export function UnitTypeNew() {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [unitType, setUnitType] = useState<
    IPostAccommodationUnitType & {
      description: string;
    }
  >({
    title: "",
    base_occupancy: undefined,
    max_occupancy: undefined,
    category_id: undefined,
    description: "",
  });

  const [accommodationCategories, setAccommodationCategories] = useState(undefined);

  useEffect(() => {
    void loadAccommodationCategories();
  }, []);

  async function createAccommodationUnitType() {
    console.log(unitType);

    const unitTypeRes = await PostAccommodationUnitType(
      {
        title: unitType?.title,
        base_occupancy: unitType?.base_occupancy,
        max_occupancy: unitType?.max_occupancy,
        category_id: unitType?.category_id,
      },

      true,
    );

    if (unitTypeRes.status !== StatusCode.OK) {
      dispatch(showError("Error creating new unit type"));
      return;
    }

    dispatch(showSuccess(t("Successfully created unit type")));
    history.push("/admin/accommodation/configuration/unit-types");
  }

  async function loadAccommodationCategories() {
    if (accommodationCategories !== undefined) {
      setAccommodationCategories(undefined);
    }
    const accommodationCategoriesRes = await GetAccommodationCategories(null, false);

    if (accommodationCategoriesRes.status !== StatusCode.OK) {
      setAccommodationCategories([]);
      return;
    }

    setAccommodationCategories(accommodationCategoriesRes.data);
  }

  return (
    <Page
      title={t("Add New Unit Type")}
      subtitle={t("Enter the key details for your unit type")}
      breadcrumbs={[
        { label: t("Accommodations"), url: "/admin/accommodation" },
        { label: t("Property Configuration"), url: "/admin/accommodation/configuration/unit-types" },
        { label: t("Unit Types"), url: "/admin/accommodation/configuration/unit-types" },
      ]}
    >
      <Form>
        <Card title="General Information">
          <div style={{ paddingLeft: "24px", paddingRight: "24px" }}>
            <hr style={{ marginBottom: "0px" }} />
          </div>

          <Card.Section>
            <FormLayout>
              <FormLayout.Group>
                <Input
                  value={unitType?.title}
                  label={t("Name of unit type")}
                  id="title"
                  onChange={e => handleChangeEventInput(e, unitType, setUnitType)}
                  placeholder={t("Enter unit type name")}
                />

                <Select
                  label={t("Category")}
                  onChange={(value: number) => setUnitType(prev => ({ ...prev, category_id: Number(value) }))}
                  defaultValue={unitType?.category_id}
                  placeholder="Select category"
                >
                  {accommodationCategories?.map((category: IAccommodationCategory) => (
                    <Select.Option key={category.id} value={category.id} name={category.title}>
                      {category.title}
                    </Select.Option>
                  ))}
                </Select>
              </FormLayout.Group>

              <FormLayout.Group>
                <Input
                  value={unitType?.base_occupancy}
                  label={t("Base occupancy")}
                  id="base_occupancy"
                  onChange={e => handleChangeEventInput(e, unitType, setUnitType)}
                  placeholder={"Minimum occupancy"}
                  type={"number"}
                />
                <Input
                  value={unitType?.max_occupancy}
                  label={t("Max. occupancy")}
                  id="max_occupancy"
                  onChange={e => handleChangeEventInput(e, unitType, setUnitType)}
                  placeholder={"Maximum occupancy"}
                  type={"number"}
                />
              </FormLayout.Group>

              <FormLayout.Group>
                <div>
                  <TextField
                    label={t("Description")}
                    value={unitType?.description ? unitType?.description : ""}
                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                      setUnitType(prev => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    maxLength={300}
                    characterCountBottom
                    rows={5}
                    placeholder="Enter unit type description"
                  />
                </div>
              </FormLayout.Group>
            </FormLayout>
          </Card.Section>
        </Card>

        <FormLayout>
          <FormLayout.Group>
            <div></div>
            <div></div>
            <div className="flex justify-end mt-3">
              <Button type="secondary" onClick={() => history.push("/admin/accommodation/configuration/unit-types")}>
                {"Cancel"}
              </Button>

              <Button type="primary" onClick={() => createAccommodationUnitType()}>
                {"Publish"}
              </Button>
            </div>
          </FormLayout.Group>
        </FormLayout>
      </Form>
    </Page>
  );
}
